import { CircleAlert, CircleCheck, Info, TriangleAlert, X } from 'lucide-react';
import { createContext, useCallback, useContext, useMemo, useRef, useState, type ReactNode } from 'react';
import clsx from 'clsx';

type Kind = 'success' | 'error' | 'info' | 'warning';
interface Toast {
  id: number;
  kind: Kind;
  message: ReactNode;
}
interface Api {
  show: (message: ReactNode, kind?: Kind, ms?: number) => void;
  success: (m: ReactNode) => void;
  error: (m: ReactNode) => void;
  info: (m: ReactNode, ms?: number) => void;
  warning: (m: ReactNode) => void;
}

const ToastContext = createContext<Api | null>(null);

const icons = { success: CircleCheck, error: CircleAlert, info: Info, warning: TriangleAlert };
const tones = { success: 'text-emerald-500', error: 'text-rose-500', info: 'text-brand-500', warning: 'text-amber-500' };

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([]);
  const seq = useRef(0);

  const dismiss = useCallback((id: number) => setToasts((t) => t.filter((x) => x.id !== id)), []);
  const show = useCallback(
    (message: ReactNode, kind: Kind = 'success', ms = 4000) => {
      const id = ++seq.current;
      setToasts((t) => [...t.slice(-3), { id, kind, message }]);
      window.setTimeout(() => dismiss(id), ms);
    },
    [dismiss],
  );

  const api = useMemo<Api>(
    () => ({
      show,
      success: (m) => show(m, 'success'),
      error: (m) => show(m, 'error', 6000),
      info: (m, ms) => show(m, 'info', ms ?? 5000),
      warning: (m) => show(m, 'warning', 5000),
    }),
    [show],
  );

  return (
    <ToastContext.Provider value={api}>
      {children}
      <div aria-live="polite" className="pointer-events-none fixed inset-x-0 top-3 z-[2000] flex flex-col items-center gap-2 px-4 sm:left-auto sm:right-4 sm:top-4 sm:items-end">
        {toasts.map((t) => {
          const I = icons[t.kind];
          return (
            <div key={t.id} role={t.kind === 'error' ? 'alert' : 'status'} className="pointer-events-auto flex w-full max-w-sm animate-fade-in items-start gap-3 rounded-xl border border-slate-200 bg-white p-3.5 text-sm shadow-pop dark:border-ink-700 dark:bg-ink-800">
              <I className={clsx('mt-0.5 h-5 w-5 shrink-0', tones[t.kind])} aria-hidden />
              <div className="flex-1 text-slate-700 dark:text-slate-200">{t.message}</div>
              <button onClick={() => dismiss(t.id)} aria-label="Dismiss" className="text-slate-400 hover:text-slate-600 dark:hover:text-slate-200">
                <X className="h-4 w-4" />
              </button>
            </div>
          );
        })}
      </div>
    </ToastContext.Provider>
  );
}

export function useToast(): Api {
  const c = useContext(ToastContext);
  if (!c) throw new Error('useToast must be used inside <ToastProvider>');
  return c;
}
